import React, { useEffect, useState } from 'react';
import { Trophy, Lock, CheckCircle, Star } from 'lucide-react';
import { getAchievements, getTotalPoints } from '../services/achievementService';
import { Achievement, AchievementCategory } from '../types/achievement';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const AchievementsPage = () => {
    const [achievements, setAchievements] = useState<Achievement[]>([]);
    const [totalPoints, setTotalPoints] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | AchievementCategory>('all');

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            const data = await getAchievements();
            const points = await getTotalPoints();
            setAchievements(data);
            setTotalPoints(points);
            setLoading(false);
        };
        loadData();
    }, []);

    const unlockedCount = achievements.filter(a => a.unlocked).length;
    const progressPercentage = (unlockedCount / achievements.length) * 100;

    const filteredAchievements = filter === 'all'
        ? achievements
        : achievements.filter(a => a.category === filter);

    const categories: { id: 'all' | AchievementCategory; label: string }[] = [
        { id: 'all', label: 'Hepsi' },
        { id: 'profil', label: 'Profil' },
        { id: 'kesif', label: 'Keşif' },
        { id: 'topluluk', label: 'Topluluk' },
    ];

    return (
        <div className="min-h-screen bg-[#f5fefd] py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">

                {/* Header Section */}
                <div className="text-center mb-12">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
                        <Trophy className="w-10 h-10 text-yellow-500" />
                        Başarımlarım
                    </h1>
                    <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                        Bu sayfa, Say-Hot ile yaptığın keşiflerden kazandığın rozetleri gösterir.
                        Daha çok keşfet, daha çok kazan!
                    </p>
                </div>

                {/* Progress & Stats Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 mb-10">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Genel İlerleme</h2>
                            <p className="text-slate-500 text-sm">Toplam {achievements.length} başarımdan {unlockedCount} tanesini tamamladın.</p>
                        </div>
                        <div className="flex items-center gap-2 bg-yellow-50 px-4 py-2 rounded-full border border-yellow-100">
                            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                            <span className="font-bold text-yellow-700">{totalPoints} Puan</span>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden">
                        <div
                            className="bg-gradient-to-r from-blue-500 to-cyan-400 h-full rounded-full transition-all duration-1000 ease-out"
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex flex-wrap justify-center gap-2 mb-10">
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setFilter(cat.id)}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${filter === cat.id
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                                }`}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>

                {/* Achievements Grid */}
                {loading ? (
                    <div className="text-center py-20 text-slate-500">Yükleniyor...</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredAchievements.map((achievement) => (
                            <div
                                key={achievement.id}
                                className={`relative overflow-hidden rounded-xl p-6 border transition-all duration-300 ${achievement.unlocked
                                    ? 'bg-white border-blue-100 shadow-sm hover:shadow-md'
                                    : 'bg-slate-50 border-slate-100 opacity-75 grayscale-[0.5]'
                                    }`}
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${achievement.unlocked ? 'bg-blue-50' : 'bg-slate-200'
                                        }`}>
                                        {achievement.icon || '🏆'}
                                    </div>
                                    <div className={`px-2 py-1 rounded text-xs font-bold ${achievement.unlocked
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-slate-200 text-slate-500'
                                        }`}>
                                        {achievement.points} P
                                    </div>
                                </div>

                                <h3 className={`text-lg font-bold mb-2 ${achievement.unlocked ? 'text-gray-900' : 'text-slate-600'}`}>
                                    {achievement.title}
                                </h3>
                                <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                                    {achievement.description}
                                </p>

                                <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-100">
                                    {achievement.unlocked ? (
                                        <div className="flex items-center text-green-600 text-xs font-semibold">
                                            <CheckCircle className="w-4 h-4 mr-1" />
                                            <span>Tamamlandı</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center text-slate-400 text-xs font-semibold">
                                            <Lock className="w-4 h-4 mr-1" />
                                            <span>Kilitli</span>
                                        </div>
                                    )}

                                    {achievement.unlocked && achievement.unlockedAt && (
                                        <span className="text-xs text-slate-400">
                                            {format(achievement.unlockedAt, 'd MMM yyyy', { locale: tr })}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AchievementsPage;
